export const supplementaryDataTabAvailable = ({ featureDetails }) => {
    const hasLinkedFiles = featureDetails?.linkedFiles?.length;
    const hasGuidances = featureDetails?.guidances?.length;

    return (hasLinkedFiles || hasGuidances);
};

const CONTACT_TYPES = ['business', 'emergency'];
const CONTACT_DETAIL_FIELDS = ['telephoneNumber', 'emailAddress', 'webForm', 'singleLineAddress'];

const CONTACT_SOURCE_KEYS = {
    asset: ['assetContacts', 'assetContact', 'assetContactDetails', 'assetContactRecords'],
    assetOwner: ['assetOwnerContacts', 'ownerContacts', 'organisationContacts', 'organizationContacts', 'dataProviderContacts']
};

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasValue = (value) => typeof value === 'string' ? value.trim().length > 0 : Boolean(value);

const hasContactInfo = (value) => {
    return CONTACT_DETAIL_FIELDS.some(field => hasValue(value?.[field]));
};

const normaliseContactType = (value) => {
    const contactType = value?.toLowerCase?.();
    return CONTACT_TYPES.includes(contactType) ? contactType : null;
};

export const normaliseContactsOfType = (contactsOfType) => {
    if (Array.isArray(contactsOfType)) {
        return contactsOfType.filter(hasContactInfo);
    }

    if (isObject(contactsOfType) && hasContactInfo(contactsOfType)) {
        return [contactsOfType];
    }

    return [];
};

const createEmptyContactMap = () => ({
    business: [],
    emergency: []
});

const mergeContacts = (targetContacts, contactsToMerge) => {
    CONTACT_TYPES.forEach(contactType => {
        targetContacts[contactType] = [
            ...targetContacts[contactType],
            ...normaliseContactsOfType(contactsToMerge?.[contactType])
        ];
    });

    return targetContacts;
};

const normaliseContacts = (rawContacts, preferredContactType = null) => {
    const contacts = createEmptyContactMap();

    if (!rawContacts) {
        return contacts;
    }

    if (Array.isArray(rawContacts)) {
        rawContacts.forEach(contact => {
            if (!contact) {
                return;
            }

            if (hasContactInfo(contact)) {
                const contactType = normaliseContactType(contact.contactType || contact.type) || preferredContactType;
                if (contactType) {
                    contacts[contactType].push(contact);
                }
                return;
            }

            if (isObject(contact)) {
                mergeContacts(contacts, normaliseContacts(contact, preferredContactType));
            }
        });

        return contacts;
    }

    if (isObject(rawContacts)) {
        if (CONTACT_TYPES.some(contactType => rawContacts[contactType] !== undefined)) {
            return mergeContacts(contacts, rawContacts);
        }

        if (rawContacts.contacts !== undefined) {
            return mergeContacts(contacts, normaliseContacts(rawContacts.contacts, preferredContactType));
        }

        if (hasContactInfo(rawContacts)) {
            const contactType = normaliseContactType(rawContacts.contactType || rawContacts.type) || preferredContactType;
            if (contactType) {
                contacts[contactType].push(rawContacts);
            }
        }
    }

    return contacts;
};

const getSectionHeading = (section, fallbackHeading) => {
    return section?.heading ||
        section?.label ||
        section?.name ||
        section?.title ||
        section?.assetName ||
        section?.displayName ||
        fallbackHeading ||
        null;
};

const normaliseDisplayName = (value) => {
    if (typeof value === 'string') {
        return value;
    }

    if (isObject(value)) {
        return value.en || value.cy || null;
    }

    return null;
};

const createContactSection = ({ sourceType, rawSection, fallbackHeading, preferredContactType = null }) => {
    const contacts = normaliseContacts(rawSection?.contacts ?? rawSection, preferredContactType);
    const hasContacts = CONTACT_TYPES.some(contactType => contacts[contactType].length > 0);

    if (!hasContacts) {
        return null;
    }

    return {
        sourceType,
        heading: normaliseDisplayName(getSectionHeading(rawSection, fallbackHeading)),
        contacts
    };
};

const flattenSourceSections = (rawSource, sourceType, fallbackHeading) => {
    if (!rawSource) {
        return [];
    }

    if (Array.isArray(rawSource)) {
        const sections = rawSource.every(item => hasContactInfo(item) || normaliseContactType(item?.contactType || item?.type))
            ? [createContactSection({ sourceType, rawSection: rawSource, fallbackHeading })]
            : rawSource.map(section => createContactSection({ sourceType, rawSection: section, fallbackHeading }));

        return sections.filter(Boolean);
    }

    if (isObject(rawSource)) {
        if (rawSource.contacts !== undefined || CONTACT_TYPES.some(contactType => rawSource[contactType] !== undefined) || hasContactInfo(rawSource)) {
            return [createContactSection({ sourceType, rawSection: rawSource, fallbackHeading })].filter(Boolean);
        }

        return Object.values(rawSource)
            .map(section => createContactSection({ sourceType, rawSection: section, fallbackHeading }))
            .filter(Boolean);
    }

    return [];
};

const getExplicitContactSections = (supplementaryDataEntry = {}) => {
    return Object.entries(CONTACT_SOURCE_KEYS).flatMap(([sourceType, keys]) => {
        return keys.flatMap(key => flattenSourceSections(supplementaryDataEntry?.[key], sourceType));
    });
};

const dedupeContactSections = (sections) => {
    const seen = new Set();

    return sections.filter(section => {
        const signature = JSON.stringify(section);
        if (seen.has(signature)) {
            return false;
        }

        seen.add(signature);
        return true;
    });
};

export const getContactSections = ({
    supplementaryDataEntries = [],
    assetOwnerName = null,
    ownerContacts = null,
    assetContacts = null,
    supplementaryContactsSource = 'assetOwner'
} = {}) => {
    const orderedSections = [];

    const assetContactSection = createContactSection({
        sourceType: 'asset',
        rawSection: { contacts: assetContacts }
    });

    if (assetContactSection) {
        orderedSections.push(assetContactSection);
    }

    supplementaryDataEntries.forEach(entry => {
        const explicitSections = getExplicitContactSections(entry);
        orderedSections.push(...explicitSections.filter(section => section.sourceType !== 'asset'));

        if (supplementaryContactsSource === 'asset' && !assetContactSection) {
            orderedSections.push(...flattenSourceSections(entry?.contacts, 'asset'));
        } else if (supplementaryContactsSource === 'assetOwner') {
            orderedSections.push(...flattenSourceSections(entry?.contacts, 'assetOwner', assetOwnerName));
        }
    });

    const ownerContactSection = createContactSection({
        sourceType: 'assetOwner',
        rawSection: { heading: assetOwnerName, contacts: ownerContacts },
        fallbackHeading: assetOwnerName
    });

    if (ownerContactSection) {
        orderedSections.push(ownerContactSection);
    }

    const orderedBySource = [
        ...orderedSections.filter(section => section.sourceType === 'asset'),
        ...orderedSections.filter(section => section.sourceType === 'assetOwner')
    ];

    return dedupeContactSections(orderedBySource);
};
